import { safeSlug } from "@/lib/utils"
import { localizeUrl, isExternalUrl } from "@/lib/url/localizeUrl"
import { buildRcUrl } from "@/lib/url/buildRcUrl"
import { getContentPrefixOrDefault } from "@/lib/url/blogPrefixMap"
import { DEFAULT_PUBLIC_LANG } from "@/lib/locale/toContentfulLocale"

const RC_TYPENAMES = new Set([
  "Article",
  "CaseStudy",
  "CustomerStory",
  "Video",
  "Document",
])

export const processLinkUrl = (
  link: any,
  lang: string = DEFAULT_PUBLIC_LANG,
  blogPrefix?: string,
) => {
  if (link?.__typename === "ExternalLink") {
    return link?.url || null
  }
  if (link?.__typename === "PageProductPage") {
    return localizeUrl(`/products/${safeSlug(link?.slug)}`, lang)
  }
  if (link?.__typename === "ProductDetailPage") {
    // `slug` is the source of truth for this page's URL — no link without one.
    return link?.slug ? localizeUrl(`/products/${link.slug}/buy`, lang) : null
  }
  if (link?.__typename === "ProductTrialPage") {
    // `slug` is the source of truth for this page's URL — no link without one.
    return link?.slug
      ? localizeUrl(`/products/${link.slug}/trial`, lang)
      : null
  }
  if (RC_TYPENAMES.has(link?.__typename)) {
    const prefix =
      blogPrefix ?? getContentPrefixOrDefault(link, link.__typename)
    return buildRcUrl(lang, link.__typename, link?.slug, prefix)
  } else if (link?.slug) {
    return localizeUrl(`/${safeSlug(link?.slug)}`, lang)
  } else if (typeof link?.url === "string" && link.url !== "") {
    if (link.url.startsWith("#")) return link.url
    if (isExternalUrl(link.url)) return link.url
    return localizeUrl(
      link.url.startsWith("/") ? link.url : `/${link.url}`,
      lang,
    )
  } else if (typeof link === "string") {
    if (link.startsWith("#")) return link
    if (isExternalUrl(link)) return link
    return localizeUrl(link.startsWith("/") ? link : `/${link}`, lang)
  }
  return null
}

/**
 * Resolves a Contentful `Link` entry (as fetched via `LinkFragment` in
 * `src/queries/atoms/linkFragment.ts`) attached to an `Image`/`ImageWithFocalPoint`
 * to a clickable href, or `null` when no destination can be determined.
 */
export const resolveImageLinkHref = (
  link: any,
  lang: string = DEFAULT_PUBLIC_LANG,
): string | null => {
  if (!link) return null
  if (link.url) {
    const resolved = processLinkUrl(link.url, lang)
    if (resolved) return resolved
  }
  if (link.anchor) return `#${link.anchor}`
  if (link.internalLink) return processLinkUrl(link.internalLink, lang)
  return null
}

export const resolveContentfulRichTextLinks = (content: any) => {
  if (!content?.json?.content) return content

  content.json.content = content.json.content.map((node: any) => {
    try {
      // Resolve embedded assets
      if (node.nodeType === "embedded-asset-block") {
        const foundAsset = content.links?.assets?.block?.find(
          (obj: any) => obj?.sys?.id === node.data?.target?.sys?.id,
        )
        if (foundAsset) node.data.target = foundAsset
      }

      // Resolve embedded entries
      if (node.nodeType === "embedded-entry-block") {
        const foundEntry = content.links?.entries?.block?.find(
          (obj: any) => obj?.sys?.id === node.data?.target?.sys?.id,
        )
        if (foundEntry) node.data.target = foundEntry
      }
    } catch (err) {
      console.log(err)
    }
    return node
  })

  return content
}
